import React, { useRef, useState } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";

const G_LOGO = "https://www.gstatic.com/marketing-cms/assets/images/d5/dc/cfe9ce8b4425b410b49b7f2dd3f3/g.webp=s96-fcrop64=1,00000000ffffffff-rw";

export const Stars = ({ n = 0, size = 14, className = "text-red-500" }) => (
  <div className={`flex gap-0.5 ${className}`} aria-label={`${n} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map(i => <Star key={i} size={size} fill={i <= Math.round(n) ? "currentColor" : "none"} className={i <= Math.round(n) ? "" : "opacity-30"} />)}
  </div>
);

function ReviewCard({ r, idx, testPrefix }) {
  const [open, setOpen] = useState(false);
  const long = (r.text || "").length > 220;
  return (
    <article className="snap-start shrink-0 w-[85vw] sm:w-[360px] lg:w-[400px] card-surface p-7 flex flex-col" data-testid={`${testPrefix}-${idx}`}>
      <div className="flex items-center gap-3 mb-4">
        {r.author_photo
          ? <img src={r.author_photo} alt="" className="w-10 h-10 rounded-full object-cover" referrerPolicy="no-referrer" loading="lazy" />
          : <div className="w-10 h-10 rounded-full bg-red-600/20 text-red-500 grid place-items-center font-display text-lg">{(r.author_name || "G")[0]}</div>}
        <div className="min-w-0">
          <div className="text-white text-sm font-medium truncate" data-testid={`${testPrefix}-${idx}-author`}>{r.author_name}</div>
          <div className="text-zinc-500 text-[11px]" data-testid={`${testPrefix}-${idx}-time`}>{r.relative_time_description}</div>
        </div>
        <img src={G_LOGO} alt="Google" className="h-4 w-4 ml-auto opacity-70" />
      </div>
      <Stars n={r.rating || 5} size={13} />
      <p className={`text-zinc-300 text-sm mt-4 leading-relaxed ${open ? "" : "line-clamp-5"}`} data-testid={`${testPrefix}-${idx}-text`}>{r.text}</p>
      {long && (
        <button onClick={() => setOpen(o => !o)} className="text-red-500 text-[10px] uppercase tracking-widest font-bold mt-3 text-left" data-testid={`${testPrefix}-${idx}-more`}>
          {open ? "Show less" : "Read more"}
        </button>
      )}
    </article>
  );
}

// Rating summary + horizontal snap carousel. `data` is the /api/google-reviews payload.
export default function GoogleReviewsCarousel({ data, testPrefix = "google-review", reviewUrl: reviewUrlProp }) {
  const track = useRef(null);
  const reviewUrl = reviewUrlProp || data?.write_review_uri || data?.google_maps_uri || "https://share.google/AZ1RxzCPdFjq4J2tm";
  const reviews = Array.isArray(data?.reviews) ? data.reviews : [];
  if (!data?.enabled || reviews.length === 0) return null;
  const scroll = (dir) => track.current?.scrollBy({ left: dir * (track.current.clientWidth * 0.8), behavior: "smooth" });

  return (
    <div data-testid="google-reviews-carousel">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <a href={reviewUrl} target="_blank" rel="noreferrer" className="flex items-center gap-5 group/rating" title="Read & leave reviews on Google" data-testid="google-rating-link">
          <div className="font-display text-6xl md:text-7xl leading-none text-white group-hover/rating:text-red-500 transition-colors" data-testid="google-avg-rating">{data.rating != null ? Number(data.rating).toFixed(1) : "—"}</div>
          <div>
            <Stars n={data.rating || 0} size={18} />
            <div className="text-zinc-400 text-sm mt-2 flex items-center gap-2 group-hover/rating:text-white transition-colors">
              <img src={G_LOGO} alt="Google" className="h-4 w-4" />
              <span data-testid="google-total-reviews">{data.user_ratings_total ?? 0} Google reviews</span>
            </div>
          </div>
        </a>
        <div className="flex items-center gap-3">
          <a href={reviewUrl} target="_blank" rel="noreferrer" className="btn-ghost !py-2 !px-4 !text-xs" data-testid="google-maps-link">Leave a Google review</a>
          <div className="hidden sm:flex gap-2">
            <button onClick={() => scroll(-1)} aria-label="Previous reviews" className="w-10 h-10 border border-white/15 grid place-items-center hover:border-red-600 hover:text-red-500 transition-colors" data-testid="reviews-prev"><ChevronLeft size={16} /></button>
            <button onClick={() => scroll(1)} aria-label="Next reviews" className="w-10 h-10 border border-white/15 grid place-items-center hover:border-red-600 hover:text-red-500 transition-colors" data-testid="reviews-next"><ChevronRight size={16} /></button>
          </div>
        </div>
      </div>
      <div ref={track} className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-6 px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="reviews-track">
        {reviews.map((r, i) => <ReviewCard key={`${r.author_name}-${i}`} r={r} idx={i} testPrefix={testPrefix} />)}
      </div>
    </div>
  );
}
